import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGrowthOnePagerEditor } from '@/contexts/GrowthOnePagerEditorContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useSidebar } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ShoppingCart,
  Target,
  TrendingUp,
  Star,
  X,
  RefreshCw,
} from 'lucide-react';
import SalesChannelCard from './SalesChannelCard';

export function GlobalGrowthOnePagerEditor() {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const { isOpen, closeEditor, salesChannels, stats, isLoading, refetch } = useGrowthOnePagerEditor();
  const { currentBrand } = useBrandManager();
  const { state: sidebarState, isMobile } = useSidebar();
  const location = useLocation();

  // Close drawer when navigating away from /gh-onepager
  useEffect(() => {
    if (isOpen && location.pathname !== '/gh-onepager') {
      closeEditor();
    }
  }, [location.pathname]);

  // Calculate drawer width and top offset based on sidebar state
  const SIDEBAR_WIDTH = '16rem';
  const SIDEBAR_WIDTH_ICON = '3rem';
  const MOBILE_HEADER_HEIGHT = '57px';
  
  const drawerWidth = isMobile 
    ? '100%' 
    : sidebarState === 'expanded' 
      ? `calc(100% - ${SIDEBAR_WIDTH})` 
      : `calc(100% - ${SIDEBAR_WIDTH_ICON})`;
  
  const drawerTop = isMobile ? MOBILE_HEADER_HEIGHT : '0';

  // Scroll to top when drawer opens
  useEffect(() => {
    if (isOpen && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [isOpen]);

  const renderLoadingState = () => (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12 mb-1" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
      <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold mb-2">No Sales Channels</h3>
      <p className="text-muted-foreground mb-4">
        Create sales channels to see your growth strategy overview.
      </p>
    </div>
  );

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && closeEditor()} direction="right" modal={false}>
      <DrawerContent 
        className="max-w-none right-0 left-auto rounded-none mt-0 fixed bg-background border-l"
        style={{ 
          width: drawerWidth, 
          top: drawerTop,
          height: isMobile ? `calc(100% - ${MOBILE_HEADER_HEIGHT})` : '100%'
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header - Mobile: stacked rows, Desktop: horizontal */}
          <div className="px-4 sm:px-6 py-4 border-b">
            {/* Mobile Layout */}
            <div className="sm:hidden">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Growth Modelling</h2>
                <Button variant="ghost" size="icon" onClick={closeEditor}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                Sales channels, pillars & metrics
                {currentBrand && <span className="ml-1 text-primary">({currentBrand.name})</span>}
              </p>
              <div className="flex justify-end pt-1">
                <Button onClick={refetch} disabled={isLoading} size="sm" variant="outline">
                  <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-semibold">Growth Modelling</h2>
                  <p className="text-sm text-muted-foreground">
                    View sales channels, growth pillars and metrics
                    {currentBrand && <span className="ml-2 text-primary">({currentBrand.name})</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={refetch} disabled={isLoading} variant="outline">
                  <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button variant="ghost" size="icon" onClick={closeEditor}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <ScrollArea ref={scrollAreaRef} className="flex-1">
            {isLoading ? (
              renderLoadingState()
            ) : salesChannels.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className="space-y-6 p-4 sm:p-6">
                {/* Header Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-blue-800 dark:text-blue-200">Sales Channels</CardTitle>
                      <ShoppingCart className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.totalSalesChannels}</div>
                      <p className="text-xs text-blue-700 dark:text-blue-300">Active channels</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-green-800 dark:text-green-200">Growth Pillars</CardTitle>
                      <Target className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-900 dark:text-green-100">{stats.totalGrowthPillars}</div>
                      <p className="text-xs text-green-700 dark:text-green-300">Total pillars</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-purple-800 dark:text-purple-200">Growth Metrics</CardTitle>
                      <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <Badge variant="secondary" className="flex items-center justify-start w-full text-xs border-0">
                          <Star className="h-3 w-3 mr-1 text-yellow-500" />
                          North Star ({stats.northStarMetrics})
                        </Badge>
                        <Badge variant="secondary" className="flex items-center justify-start w-full text-xs border-0">
                          <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
                          Supportive ({stats.supportiveMetrics})
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Sales Channels Hierarchy */}
                <div className="space-y-6">
                  {salesChannels.map((channel) => (
                    <SalesChannelCard key={channel.id} channel={channel} />
                  ))}
                </div>
              </div>
            )}
          </ScrollArea>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
